import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { JobCommande } from '../types/entities';
import { RootState } from './store';
import { baseUrlApi } from '../../utils/url';

interface JobCommandeState {
  jobcommandes: JobCommande[];
  jobcommande: JobCommande | null;
  loading: boolean;
  error: string | null;
}

const initialState: JobCommandeState = {
  jobcommandes: [],
  jobcommande: null,
  loading: false,
  error: null,
};

export const fetchJobCommandes = createAsyncThunk<JobCommande[], void, { state: RootState }>(
  'jobcommande/fetchJobCommandes',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      const response = await axios.get(`${baseUrlApi}/jobcommandes/all/${authentification.user.OwnerId}`, {
        headers: { Authorization: `bearer ${authentification.user.token}` }
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch jobcommandes');
    }
  }
);

// ✅ BUG CORRIGÉ : utiliser ${jobcommandeId} au lieu de ${'jobcommandeId'}
export const fetchJobCommande = createAsyncThunk<JobCommande, number, { state: RootState }>(
  'jobcommande/fetchJobCommande',
  async (jobcommandeId, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      // ✅ Correction : utiliser la variable jobcommandeId, pas la string 'jobcommandeId'
      const response = await axios.get(`${baseUrlApi}/jobcommandes/${jobcommandeId}`, {
        headers: { Authorization: `bearer ${authentification.user.token}` }
      });
      dispatch(fetchJobCommandes());
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch jobcommande');
    }
  }
);

export const createJobCommande = createAsyncThunk<JobCommande, Partial<JobCommande>, { state: RootState }>(
  'jobcommande/createJobCommande',
  async (jobcommandeData, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      const response = await axios.post(`${baseUrlApi}/jobcommandes/`, jobcommandeData, {
        headers: { Authorization: `bearer ${authentification.user.token}` }
      });
      dispatch(fetchJobCommandes());
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create jobcommande');
    }
  }
);

// ✅ BUG CORRIGÉ : utiliser ${jobcommandeId} au lieu de ${'jobcommandeId'}
export const updateJobCommande = createAsyncThunk<
  JobCommande,
  { jobcommandeData: Partial<JobCommande>; 
    jobcommandeId: number },
  { state: RootState }
>(
  'jobcommande/updateJobCommande',
  async ({ jobcommandeData, jobcommandeId }, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      // ✅ Correction : utiliser la variable jobcommandeId, pas la string
      const response = await axios.put(`${baseUrlApi}/jobcommandes/${jobcommandeId}`, jobcommandeData, {
        headers: { Authorization: `bearer ${authentification.user.token}` }
      });
      dispatch(fetchJobCommandes());
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update jobcommande');
    }
  }
);

// ✅ BUG CORRIGÉ : utiliser ${jobcommandeId} au lieu de ${'jobcommandeId'}
export const deleteJobCommande = createAsyncThunk<number, number, { state: RootState }>(
  'jobcommande/deleteJobCommande',
  async (jobcommandeId, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      // ✅ Correction : utiliser la variable jobcommandeId, pas la string
      await axios.delete(`${baseUrlApi}/jobcommandes/${jobcommandeId}`, {
        headers: { Authorization: `bearer ${authentification.user.token}` }
      });
      dispatch(fetchJobCommandes());
      return jobcommandeId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete jobcommande');
    }
  }
);

const jobcommandeSlice = createSlice({
  name: 'jobcommande',
  initialState,
  reducers: {
    clearJobCommande: (state) => { state.jobcommande = null; },
    clearJobCommandes: (state) => { state.jobcommandes = []; },
    resetJobCommandeState: () => initialState,
  },
  extraReducers: (builder) => {
    [fetchJobCommandes, fetchJobCommande, createJobCommande, updateJobCommande, deleteJobCommande].forEach((thunk) => {
      builder
        .addCase(thunk.pending, (state) => {
          state.loading = true;
          state.error = null;
        })
        .addCase(thunk.rejected, (state, action) => {
          state.loading = false;
          state.error = action.payload as string;
        });
    });

    builder
      .addCase(fetchJobCommandes.fulfilled, (state, action) => {
        state.loading = false;
        state.jobcommandes = action.payload;
      })
      .addCase(fetchJobCommande.fulfilled, (state, action) => {
        state.loading = false;
        state.jobcommande = action.payload;
      })
      .addCase(createJobCommande.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateJobCommande.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deleteJobCommande.fulfilled, (state, action) => {
        state.loading = false;
        state.jobcommandes = state.jobcommandes.filter(item => item.id !== action.payload);
      });
  },
});

export const { clearJobCommande, clearJobCommandes, resetJobCommandeState } = jobcommandeSlice.actions;
export default jobcommandeSlice.reducer;